import React from 'react';
import { useFormikContext } from 'formik';

import MenuItem from '~/components/core/Atomic/MenuItem';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import Text from '~/components/core/TextComponents/Text';
import type {
  DocumentTemplatesSearchParams,
  EmailTemplateFormSubmitValuesProps,
  TemplatesApiResponse,
} from '~/components/GenericTemplates/types';
import { DOCUMENT_TEMPLATES_ROUTES } from '~/components/GenericTemplates/utils/routes';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { getAxiosParamsSerializer, isFeatureEnabled } from '~/Utils';

const FETCH_PARAMS_SERIALIZER = getAxiosParamsSerializer('none');

export interface EmailTemplateDocumentSelectorProps {
  documentTemplateFieldId: 'document_template_id';
}

const EmailTemplateDocumentSelector: React.FC<EmailTemplateDocumentSelectorProps> = ({ documentTemplateFieldId }) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { setFieldValue, values } = useFormikContext<EmailTemplateFormSubmitValuesProps>();
  const multiCountryOrganization = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.MULTI_COUNTRY_ORGANIZATION
  );

  const subOrgsContextReady =
    !organization.sub_organizations_enabled ||
    values.is_all_suborgs ||
    (Array.isArray(values.sub_organization_ids) && values.sub_organization_ids.length > 0);
  const lobsContextReady = values.is_all_lobs || (Array.isArray(values.lobs) && values.lobs.length > 0);
  const countriesContextReady =
    values.is_all_countries || (Array.isArray(values.countries) && values.countries.length > 0);
  const statesContextReady =
    multiCountryOrganization || values.is_all_states || (Array.isArray(values.states) && values.states.length > 0);
  const coverageContextReady = values.is_claim_level || values.is_all_coverages || values.coverage_keys?.length > 0;
  const isContextReady =
    subOrgsContextReady && lobsContextReady && countriesContextReady && statesContextReady && coverageContextReady;

  // Listen for changes to context fields and reset document_template_id
  useFormikChangeListener({
    listenForKeys: ['sub_organization_ids', 'countries', 'lobs', 'coverage_keys', 'is_claim_level', 'states'],
    onChange: () => setFieldValue(documentTemplateFieldId, null),
    runOnFirstRender: false,
  });

  const params: DocumentTemplatesSearchParams = {};

  if (organization.sub_organizations_enabled) {
    if (Array.isArray(values.sub_organization_ids) && values.sub_organization_ids.length > 0) {
      params.sub_organization_ids = values.sub_organization_ids;
    }
  }

  if (Array.isArray(values.lobs) && values.lobs.length > 0) {
    params.lobs = values.lobs;
  }
  if (Array.isArray(values.countries) && values.countries.length > 0) {
    params.country = values.countries[0];
  }
  if (Array.isArray(values.states) && values.states.length > 0) {
    params.states = values.states;
  }
  if (Array.isArray(values.coverage_keys) && values.coverage_keys.length > 0) {
    params.coverage_keys = values.coverage_keys;
  }
  if (values.is_claim_level) {
    params.is_claim_level = values.is_claim_level;
  }
  if (values.is_all_suborgs) {
    params.is_all_sub_orgs = values.is_all_suborgs;
  }
  if (values.is_all_coverages) {
    params.is_all_coverages = values.is_all_coverages;
  }

  params.is_strict = true;

  const { isLoading, isError, data } = useDataFetcher<TemplatesApiResponse>(
    DOCUMENT_TEMPLATES_ROUTES.GET_TEMPLATES(organization.id),
    {
      params,
      paramsSerializer: FETCH_PARAMS_SERIALIZER,
    },
    isContextReady
  );

  const handleDocumentTemplateChange = (value: number | null) => {
    setFieldValue(documentTemplateFieldId, value);
  };

  return (
    <div className="my-2 ml-4 mr-2 w-1/2">
      {!isError ? (
        <Text variant={Text.VARIANTS.SM} className="mb-2">
          Attach Document Template
        </Text>
      ) : (
        <Text variant={Text.VARIANTS.SM} className="mb-2" colorVariant={Text.COLOR_VARIANTS.ERROR}>
          Error loading document templates
        </Text>
      )}
      <SingleSelectField
        id={documentTemplateFieldId}
        label=""
        fullWidth
        autoSelectSingleOption={false}
        disabled={isLoading || !isContextReady || isError}
        onChange={handleDocumentTemplateChange}
        value={values[documentTemplateFieldId]}
        clearable
        clearSelection={() => handleDocumentTemplateChange(null)}
      >
        {data?.templates_metadata.map((template) => (
          <MenuItem key={template.id} value={template.id}>
            {template.template_name}
          </MenuItem>
        ))}
      </SingleSelectField>
    </div>
  );
};

export default EmailTemplateDocumentSelector;
